import React from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { PostHogProvider } from "posthog-js/react"
import { createRoot } from "react-dom/client"
import { RouterProvider } from "react-router-dom"

import "./index.css"

import { getUserInfo } from "@/features/auth/actions/auth-actions"
import { isNative } from "@/lib/capacitor"
import { hasRecentReload, markReload } from "@/lib/lazy-with-retry"
import { initPushNotifications } from "@/lib/push-notifications"
import { initializeTheme } from "@/lib/theme"
import { initFacebookPixel, setFbAdvancedMatching } from "@/lib/tracking/facebook-pixel"
import { captureMetaAttribution } from "@/lib/tracking/meta-attribution"
import { authStore } from "@/store/auth"

import { router } from "./routes/Router"
// Import Sentry configuration early
import "@/lib/sentry"

import * as Sentry from "@sentry/react"

// Bootstrap side-effects (analytics, theme, native plugins) must NEVER block the
// React render: a throw here runs before createRoot().render() and blanks the
// whole app. Each step runs isolated; failures are reported to Sentry (tagged)
// and swallowed so the app still mounts (SPEEKE-APP-E1).
function runSafely(label: string, fn: () => void) {
  try {
    fn()
  } catch (error) {
    console.error(`[bootstrap] "${label}" failed`, error)
    Sentry.captureException(error, { tags: { bootstrap: label } })
  }
}

// Initialize theme before rendering the application
runSafely("theme", initializeTheme)

// Initialize Facebook Pixel (with Advanced Matching if user already logged in)
runSafely("facebook-pixel", () => {
  const authState = authStore.getState()
  if (authState.isAuthenticated && authState.user) {
    initFacebookPixel({
      email: authState.user.email,
      name: authState.user.name,
      phone: authState.organization?.whatsapp || undefined,
      externalId: authState.user.id,
    })
  } else {
    initFacebookPixel()
  }
})

// Capture Meta attribution data (fbclid, fbc, fbp) from URL and cookies
runSafely("meta-attribution", captureMetaAttribution)

// Set Pixel Advanced Matching when user authenticates (login, Google OAuth, etc.)
runSafely("auth-subscribe", () => {
  authStore.subscribe(state => {
    if (state.isAuthenticated && state.user) {
      setFbAdvancedMatching({
        email: state.user.email,
        name: state.user.name,
        phone: state.organization?.whatsapp || undefined,
        externalId: state.user.id,
      })
    }
  })
})

// Initialize Capacitor plugins on native platforms
if (isNative) {
  runSafely("capacitor", () => {
    import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
      StatusBar.setStyle({ style: Style.Dark })
      StatusBar.setOverlaysWebView({ overlay: false })
    })
    import("@capacitor/keyboard").then(({ Keyboard }) => {
      Keyboard.setResizeMode({ mode: "body" as never })
    })
    import("@capacitor/app").then(({ App }) => {
      App.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back()
        } else {
          App.exitApp()
        }
      })
    })
    initPushNotifications()
  })
}

// Reload once when a dynamically-imported chunk fails to load. This fires when a
// new deploy has replaced the hashed asset the current tab still references, so
// the browser gets index.html (text/html) instead of the .js module. Vite emits
// vite:preloadError at the preload stage before the lazy() import rejects; the
// shared cooldown guard prevents a reload loop if a chunk is genuinely broken
// (SPEEKE-APP-BJ / CG / DP).
window.addEventListener("vite:preloadError", event => {
  if (hasRecentReload()) return
  event.preventDefault()
  markReload()
  window.location.reload()
})

// Handle auth:logout event (dispatched from axios interceptor for Capacitor compatibility)
window.addEventListener("auth:logout", () => {
  router.navigate("/auth/login")
})

// Handle push notification navigation
window.addEventListener("push:navigate", ((event: CustomEvent<{ path: string }>) => {
  router.navigate(event.detail.path)
}) as EventListener)

// Refresh feature flags when the tab regains focus. /auth/users/me returns the
// flag map computed by the backend (admin OR PostHog); refetching keeps the UI
// in sync with admin toggles without polling.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return
  if (!authStore.getState().isAuthenticated) return
  getUserInfo().catch(() => {
    // Silent failure - stale flags are non-fatal; next focus retries.
  })
})

// Create a client
const queryClient = new QueryClient()

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <PostHogProvider
        apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
        options={{
          api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
          ui_host: "https://us.posthog.com",
          debug: import.meta.env.NODE_ENV === "development",
          session_recording: {
            // Don't record cross-origin iframes (injected by browser extensions)
            recordCrossOriginIframes: false,
          },
          on_request_error: error => {
            // Silently ignore PostHog request errors to avoid noise in Sentry
            console.warn("[PostHog] Request error:", error)
          },
        }}
      >
        <RouterProvider router={router} />
      </PostHogProvider>
    </QueryClientProvider>
  </React.StrictMode>
)
